import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useApp } from "../context/AppContext";
import PageSeo from "../components/PageSeo";
import "./WriteBlog.css";

const CATEGORIES = [
  "General",
  "Riding Story",
  "Route Review",
  "Bike Review",
  "Tips & Tricks",
  "Maintenance",
  "Buying Guide",
  "Comparison",
];

function getSlugBase(title = "") {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

function getInitials(name = "") {
  if (!name) return "MH";
  return (
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "MH"
  );
}

function isValidUrl(value = "") {
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export default function WriteBlog() {
  const navigate = useNavigate();
  const { user: currentUser } = useApp();
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("General");
  const [coverImage, setCoverImage] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (currentUser) return;
    navigate("/login", {
      replace: true,
      state: { message: "Please login to write a blog" },
    });
  }, [currentUser, navigate]);

  const slugBase = useMemo(() => getSlugBase(title), [title]);
  const readTime = useMemo(() => Math.max(1, Math.ceil(content.split(/\s+/).filter(Boolean).length / 200)), [content]);
  const canSubmit = title.trim().length > 5 && content.trim().length > 200 && excerpt.trim().length > 10;

  const tagsList = useMemo(
    () =>
      tags
        .split(",")
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 5),
    [tags]
  );

  const handleSubmit = async () => {
    if (!currentUser || !canSubmit || isLoading) return;

    setIsLoading(true);
    setMessage("");

    const baseSlug = `${slugBase}-${Date.now().toString().slice(-4)}`;

    const buildPayload = (slugValue) => ({
      author_id: currentUser.id,
      title: title.trim(),
      slug: slugValue,
      excerpt: excerpt.trim(),
      content: content.trim(),
      cover_image: coverImage.trim() || null,
      category,
      tags: tagsList,
      read_time: readTime,
      status: "approved",
      published_at: new Date().toISOString(),
    });

    let insertResult = await supabase.from("blog_posts").insert(buildPayload(baseSlug));

    if (insertResult.error && /duplicate|unique/i.test(insertResult.error.message || "")) {
      const retrySlug = `${slugBase}-${Math.floor(1000 + Math.random() * 9000)}`;
      insertResult = await supabase.from("blog_posts").insert(buildPayload(retrySlug));
    }

    if (insertResult.error) {
      setMessage(insertResult.error.message || "Unable to publish blog right now.");
      setIsLoading(false);
      return;
    }

    const finalSlug = insertResult.data?.[0]?.slug || baseSlug;
    setMessage("Blog published! Redirecting...");
    setTimeout(() => {
      navigate(`/blog/${finalSlug}`);
    }, 2000);
  };

  const imagePreview = isValidUrl(coverImage) ? coverImage.trim() : "";
  const authorLabel = currentUser?.name || currentUser?.email || "MotoHub Rider";

  return (
    <>
      <PageSeo
        title="Write a Blog | MotoHub India"
        description="Share your riding story, route reviews, and bike insights with the MotoHub India community."
        path="/blog/write"
        noindex
      />

      <div className="write-page">
        <header className="write-header">
          <div className="write-shell write-header-inner">
            <button onClick={() => navigate("/blogs")} className="write-back-btn" type="button">
              Back to Blogs
            </button>
            <h1 className="write-header-title">Write a Blog</h1>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isLoading}
              className="write-publish-btn"
              type="button"
            >
              {isLoading ? "Publishing..." : "Publish"}
            </button>
          </div>
        </header>

        <main className="write-shell write-layout">
          <section className="write-form">
            <div className="write-form-card">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Your blog title..."
                maxLength={100}
                className="write-title-input"
              />
              <p className="write-counter">{title.length}/100</p>

              <div className="write-two-col">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="write-input"
                >
                  {CATEGORIES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>

                <input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="tags, comma, separated"
                  className="write-input"
                />
              </div>

              <input
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="Cover image URL (optional)"
                className="write-input"
              />
              {imagePreview ? <img src={imagePreview} alt="Cover preview" className="write-cover-preview" /> : null}

              <textarea
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Short summary (shown in blog list)..."
                rows={3}
                maxLength={200}
                className="write-textarea"
              />
              <p className="write-counter">{excerpt.length}/200</p>

              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={
                  "Write your story here...\n\nShare your experience, route details, tips, or review.\nThe more detail, the more helpful for fellow riders."
                }
                rows={20}
                className="write-textarea write-content"
              />
              <div className="write-meta-row">
                <span>{content.split(/\s+/).filter(Boolean).length} words</span>
                <span>{readTime} min read</span>
              </div>

              <div className="write-submit-row">
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit || isLoading}
                  className="write-publish-btn write-publish-main"
                  type="button"
                >
                  {isLoading ? "Publishing..." : "Submit Blog"}
                </button>
              </div>

              {message ? <div className="write-message">{message}</div> : null}
            </div>
          </section>

          <aside className="write-side">
            <div className="write-preview-card">
              <p className="write-preview-label">Preview</p>
              {imagePreview ? (
                <img src={imagePreview} alt="Preview cover" className="write-preview-image" />
              ) : (
                <div className="write-preview-placeholder">MotoHub</div>
              )}
              <div className="write-preview-body">
                <span className="write-preview-category">{category}</span>
                <h2 className="write-preview-title">{title || "Your blog title will appear here"}</h2>
                <p className="write-preview-excerpt">{excerpt || "Short summary (shown in blog list)..."}</p>
                <div className="write-preview-author">
                  <div className="write-preview-avatar">{getInitials(authorLabel)}</div>
                  <div>
                    <p className="write-preview-name">{authorLabel}</p>
                    <p className="write-preview-city">India • {readTime} min read</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="write-tips-card">
              <p className="write-tips-title">Writing Tips</p>
              <ul>
                <li>Add a catchy title with keywords</li>
                <li>Write at least 300 words for SEO</li>
                <li>Share personal experience, riders love real stories</li>
                <li>Add a cover image for more views</li>
                <li>Use specific bike names and routes</li>
              </ul>
            </div>
          </aside>
        </main>
      </div>
    </>
  );
}
